import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { db } from "../../firebase/config";

export const FREQUENT_THIRD_PARTY_LIMIT = 10;

export type FrequentThirdParty = {
  id: string;
  displayName: string;
  legalName: string;
  qstNumber: string;
  civicNumber: string;
  street: string;
  city: string;
  postalCode: string;
  phone: string;
  extension: string;
  neq: string;
  reason: string;
  signedOn: string;
  effectiveOn: string;
  expiresOn: string;
  serviceDescription: string;
  frequency: string;
  timeOfDay: string;
  lastDeclarationAt?: string;
};

export const emptyFrequentThirdParty = (): Omit<FrequentThirdParty, "id"> => ({
  displayName: "",
  legalName: "",
  qstNumber: "",
  civicNumber: "",
  street: "",
  city: "",
  postalCode: "",
  phone: "",
  extension: "",
  neq: "",
  reason: "1",
  signedOn: "",
  effectiveOn: "",
  expiresOn: "",
  serviceDescription: "",
  frequency: "",
  timeOfDay: "1",
});

export const frequentThirdPartyCollection = (storeId: string) =>
  collection(db, "stores", storeId, "frequentThirdParties");

export const partyFromDoc = (id: string, data: Record<string, unknown>): FrequentThirdParty => ({
  id,
  displayName: String(data.displayName || ""),
  legalName: String(data.legalName || ""),
  qstNumber: String(data.qstNumber || ""),
  civicNumber: String(data.civicNumber || ""),
  street: String(data.street || ""),
  city: String(data.city || ""),
  postalCode: String(data.postalCode || ""),
  phone: String(data.phone || ""),
  extension: String(data.extension || ""),
  neq: String(data.neq || ""),
  reason: String(data.reason || "1"),
  signedOn: String(data.signedOn || ""),
  effectiveOn: String(data.effectiveOn || ""),
  expiresOn: String(data.expiresOn || ""),
  serviceDescription: String(data.serviceDescription || ""),
  frequency: String(data.frequency || ""),
  timeOfDay: String(data.timeOfDay || "1"),
  lastDeclarationAt: data.lastDeclarationAt ? String(data.lastDeclarationAt) : "",
});

export async function loadFrequentThirdParties(storeId: string): Promise<FrequentThirdParty[]> {
  const snap = await getDocs(frequentThirdPartyCollection(storeId));
  return snap.docs
    .map((entry) => partyFromDoc(entry.id, entry.data() as Record<string, unknown>))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

export async function loadFrequentThirdParty(storeId: string, partyId: string): Promise<FrequentThirdParty | null> {
  if (!storeId || !partyId) return null;
  const snap = await getDoc(doc(db, "stores", storeId, "frequentThirdParties", partyId));
  if (!snap.exists()) return null;
  return partyFromDoc(snap.id, snap.data() as Record<string, unknown>);
}

export function clintFromParty(party: FrequentThirdParty) {
  return {
    nomClint: party.legalName,
    noTvqClint: party.qstNumber.replace(/\s/g, ""),
    adr: [
      {
        typAdr: "EXP",
        noCiviq: party.civicNumber,
        rue: party.street,
        vil: party.city,
        cp: party.postalCode.replace(/\s/g, "").toUpperCase(),
      },
    ],
  };
}
